import { Output, Output_Log_Level, Output_Log_Source, } from '@zattoo/taf-protobuf/protobuf/action';
import { createPlayer, EventKey, AdEventKey, } from '@zattoo/playback-sdk-react-native';
import { WebAdapterName, } from '@zattoo/playback-sdk';
import { convertMediaTrack, convertMedia, convertPlaybackState, createLog, convertLatencyMetadata, convertAdBreakData, convertAdData, convertAdMetadataData, convertAdPositionChangedData, convertAdTimeData, convertAdErrorData, convertAdPauseData, } from './utils';
const attachPlaybackStateChanged = (player, dispatchOutput) => {
    player.on(EventKey.PLAYBACK_STATE_CHANGED, (event) => {
        const state = convertPlaybackState(event.state);
        const message = Output.create({
            playerEvent: {
                playbackStateChanged: {
                    state,
                },
            },
        });
        dispatchOutput(message);
    });
};
const attachPlayerStateChanged = (player, dispatchOutput) => {
    player.on(EventKey.PLAYER_STATE_CHANGED, (event) => {
        const message = Output.create({
            playerEvent: {
                playerStateChanged: {
                    canSeekForward: event.canSeekForward,
                    canSeekBackward: event.canSeekBackward,
                    canPause: event.canPause,
                    seekableRange: event.seekableRange ?? undefined,
                },
            },
        });
        dispatchOutput(message);
    });
};
const attachAvailableAudioTrackChanged = (player, dispatchOutput) => {
    player.on(EventKey.AVAILABLE_AUDIO_TRACKS_CHANGED, (event) => {
        const message = Output.create({
            playerEvent: {
                availableAudioTracksChanged: {
                    tracks: event.tracks.map(convertMediaTrack) ?? [],
                },
            },
        });
        dispatchOutput(message);
    });
};
const attachSelectedAudioTrackChanged = (player, dispatchOutput) => {
    player.on(EventKey.SELECTED_AUDIO_TRACK_CHANGED, (event) => {
        const message = Output.create({
            playerEvent: {
                selectedAudioTrackChanged: {
                    track: event.targetTrack ? convertMediaTrack(event.targetTrack) : undefined,
                },
            },
        });
        dispatchOutput(message);
    });
};
const attachAvailableSubtitlesTrackChanged = (player, dispatchOutput) => {
    player.on(EventKey.AVAILABLE_SUBTITLES_TRACKS_CHANGED, (event) => {
        const message = Output.create({
            playerEvent: {
                availableSubtitlesTrackChanged: {
                    tracks: event.tracks.map(convertMediaTrack) ?? [],
                },
            },
        });
        dispatchOutput(message);
    });
};
const attachSelectedSubtitlesTrackChanged = (player, dispatchOutput) => {
    player.on(EventKey.SELECTED_SUBTITLES_TRACK_CHANGED, (event) => {
        const message = Output.create({
            playerEvent: {
                selectedSubtitlesTrackChanged: {
                    track: event.targetTrack ? convertMediaTrack(event.targetTrack) : undefined,
                },
            },
        });
        dispatchOutput(message);
    });
};
const attachCastMediaTracksChanged = (player, dispatchOutput) => {
    player.on(EventKey.CAST_TRACKS_CHANGED, (event) => {
        const model = {};
        for (const key of Object.keys(event.data)) {
            const trackType = key;
            const tracks = event.data[trackType];
            model[key] = {
                tracks: tracks.map((track) => {
                    return {
                        id: track.id ?? undefined,
                        language: track.language ?? undefined,
                    };
                }),
            };
        }
        const message = Output.create({
            playerEvent: {
                castMediaTrackChanged: {
                    tracks: model,
                },
            },
        });
        dispatchOutput(message);
    });
};
const attachCastMediaTrackSelectionChanged = (player, dispatchOutput) => {
    player.on(EventKey.CAST_TRACK_SELECTION_CHANGED, (event) => {
        const message = Output.create({
            playerEvent: {
                castMediaTrackSelectionChanged: {
                    ids: event.data.ids,
                },
            },
        });
        dispatchOutput(message);
    });
};
const attachPlayerReady = (player, dispatchOutput) => {
    player.on(EventKey.PLAYER_READY, () => {
        const message = Output.create({
            playerEvent: {
                playerReady: {},
            },
        });
        dispatchOutput(message);
    });
};
const attachMediaChanged = (player, dispatchOutput) => {
    player.on(EventKey.MEDIA_CHANGED, (event) => {
        const message = Output.create({
            playerEvent: {
                mediaChanged: convertMedia(event.media),
            },
        });
        dispatchOutput(message);
    });
};
const attachCurrentPositionChanged = (player, dispatchOutput) => {
    player.on(EventKey.CURRENT_POSITION_CHANGED, (event) => {
        const message = Output.create({
            playerEvent: {
                currentPositionChanged: {
                    position: event.position,
                },
            },
        });
        dispatchOutput(message);
    });
};
const attachSeeked = (player, dispatchOutput) => {
    player.on(EventKey.SEEKED, (event) => {
        const message = Output.create({
            playerEvent: {
                seeked: {
                    position: event.position,
                    seekedTime: event.seekedTime,
                },
            },
        });
        dispatchOutput(message);
    });
};
const attachPlayerError = (player, dispatchOutput) => {
    player.on(EventKey.PLAYER_ERROR, (event) => {
        const message = Output.create({
            playerEvent: {
                playerError: {
                    code: event.error.code,
                    message: event.error.message,
                },
            },
        });
        dispatchOutput(message);
    });
};
const attachQualityChanged = (player, dispatchOutput) => {
    player.on(EventKey.QUALITY_CHANGED, (event) => {
        const message = Output.create({
            playerEvent: {
                qualityChanged: {
                    bitrate: event.bitrate,
                },
            },
        });
        dispatchOutput(message);
    });
};
const attachAdEvents = (player, dispatchPlayerEvent) => {
    player.on(AdEventKey.AD_BREAK_STARTED, (event) => {
        dispatchPlayerEvent({ adBreakStarted: convertAdBreakData(event.data) });
    });
    player.on(AdEventKey.AD_BREAK_ENDED, (event) => {
        dispatchPlayerEvent({ adBreakEnded: convertAdBreakData(event.data) });
    });
    player.on(AdEventKey.AD_METADATA, (event) => {
        dispatchPlayerEvent({ adMetadata: convertAdMetadataData(event.data) });
    });
    player.on(AdEventKey.AD_LOADED, (event) => {
        dispatchPlayerEvent({ adLoaded: convertAdData(event.data) });
    });
    player.on(AdEventKey.AD_STARTED, (event) => {
        dispatchPlayerEvent({ adStarted: convertAdData(event.data) });
    });
    player.on(AdEventKey.AD_PAUSED, (event) => {
        dispatchPlayerEvent({ adPaused: convertAdTimeData(event.data) });
    });
    player.on(AdEventKey.AD_RESUMED, (event) => {
        dispatchPlayerEvent({ adResumed: convertAdTimeData(event.data) });
    });
    player.on(AdEventKey.AD_POSITION_CHANGED, (event) => {
        dispatchPlayerEvent({ adPositionChanged: convertAdPositionChangedData(event.data) });
    });
    player.on(AdEventKey.AD_ENDED, (event) => {
        dispatchPlayerEvent({ adEnded: convertAdData(event.data) });
    });
    player.on(AdEventKey.AD_SKIPPED, (event) => {
        dispatchPlayerEvent({ adSkipped: convertAdTimeData(event.data) });
    });
    player.on(AdEventKey.AD_ERROR, (event) => {
        dispatchPlayerEvent({ adError: convertAdErrorData(event.data) });
    });
    player.on(AdEventKey.AD_BREAK_SKIP_AVAILABLE, () => {
        dispatchPlayerEvent({ adBreakSkipAvailable: {} });
    });
    player.on(AdEventKey.AD_BREAK_SKIP_UNAVAILABLE, () => {
        dispatchPlayerEvent({ adBreakSkipUnavailable: {} });
    });
    player.on(AdEventKey.PAUSE_AD_PARSED, (event) => {
        dispatchPlayerEvent({ pauseAdParsed: convertAdPauseData(event.data) });
    });
    player.on(AdEventKey.AD_FIRST_QUARTILE, (event) => {
        dispatchPlayerEvent({ adFirstQuartile: convertAdData(event.data) });
    });
    player.on(AdEventKey.AD_MIDPOINT, (event) => {
        dispatchPlayerEvent({ adMidpoint: convertAdData(event.data) });
    });
    player.on(AdEventKey.AD_THIRD_QUARTILE, (event) => {
        dispatchPlayerEvent({ adThirdQuartile: convertAdData(event.data) });
    });
};
const attachLatencyChanged = (player, dispatchOutput) => {
    player.on(EventKey.LATENCY_CHANGED, (event) => {
        const message = Output.create({
            playerEvent: {
                latencyChanged: convertLatencyMetadata(event.metadata),
            },
        });
        dispatchOutput(message);
    });
};
const attachMediaEnded = (player, dispatchOutput) => {
    player.on(EventKey.MEDIA_ENDED, () => {
        const message = Output.create({
            playerEvent: {
                mediaEnded: {},
            },
        });
        dispatchOutput(message);
    });
};
const playerOptions = (options, dispatchOutput) => {
    return {
        appVersion: options.appVersion ?? '0',
        publicId: options.publicId ?? 'taf',
        appName: options.appName ?? '',
        license: options.license ?? '',
        licenseValidationUrl: options.licenseValidationUrl,
        stepBackwardDuration: options.stepBackwardDuration ?? 10,
        stepForwardDuration: options.stepForwardDuration ?? 10,
        playerAdapter: process.env.PLAYER_ADAPTER === WebAdapterName.Bitmovin
            ? WebAdapterName.Bitmovin
            : WebAdapterName.Shaka,
        muted: options.muted,
        latencyMeasurementInterval: options.latencyMeasurementInterval,
        enableOpenMeasurement: options.enableOpenMeasurement,
        enableMediaSession: options.enableMediaSession,
        logging: {
            playerLogger: {
                info: (...args) => {
                    dispatchOutput(createLog(Output_Log_Source.PLAYER, Output_Log_Level.INFO, ...args));
                },
                warn: (...args) => {
                    dispatchOutput(createLog(Output_Log_Source.PLAYER, Output_Log_Level.WARN, ...args));
                },
                error: (...args) => {
                    dispatchOutput(createLog(Output_Log_Source.PLAYER, Output_Log_Level.ERROR, ...args));
                },
            },
            adapterLogger: {
                info: (...args) => {
                    dispatchOutput(createLog(Output_Log_Source.PLAYER_ADAPTER, Output_Log_Level.INFO, ...args));
                },
                warn: (...args) => {
                    dispatchOutput(createLog(Output_Log_Source.PLAYER_ADAPTER, Output_Log_Level.WARN, ...args));
                },
                error: (...args) => {
                    dispatchOutput(createLog(Output_Log_Source.PLAYER_ADAPTER, Output_Log_Level.ERROR, ...args));
                },
            },
        },
    };
};
export const playerCreate = async (parameters, dispatchOutput) => {
    const options = playerOptions(parameters.options, dispatchOutput);
    const player = await createPlayer(options);
    const dispatchPlayerEvent = (event) => {
        dispatchOutput(Output.create({ playerEvent: event }));
    };
    attachPlaybackStateChanged(player, dispatchOutput);
    attachPlayerStateChanged(player, dispatchOutput);
    attachAvailableAudioTrackChanged(player, dispatchOutput);
    attachSelectedAudioTrackChanged(player, dispatchOutput);
    attachAvailableSubtitlesTrackChanged(player, dispatchOutput);
    attachSelectedSubtitlesTrackChanged(player, dispatchOutput);
    attachCastMediaTracksChanged(player, dispatchOutput);
    attachCastMediaTrackSelectionChanged(player, dispatchOutput);
    attachPlayerReady(player, dispatchOutput);
    attachMediaChanged(player, dispatchOutput);
    attachCurrentPositionChanged(player, dispatchOutput);
    attachSeeked(player, dispatchOutput);
    attachPlayerError(player, dispatchOutput);
    attachQualityChanged(player, dispatchOutput);
    attachLatencyChanged(player, dispatchOutput);
    attachMediaEnded(player, dispatchOutput);
    attachAdEvents(player, dispatchPlayerEvent);
    return player;
};
